import type { Manifest, ManifestStore, ValidationStatus } from '@contentauth/c2pa-web';
import {
  SCHEMA_VERSION,
  VERIFICATION_CAPABILITIES,
  verificationResultSchema,
  type Action,
  type Actor,
  type Assertion,
  type EvidenceEdge,
  type OverallStatus,
  type Tool,
  type Validity,
  type VerificationResult,
} from '@upl/evidence-schema';
import { stableEvidenceId, type AdapterContext, type ArtifactInput } from '@upl/provenance-core';
import { evaluateTrust } from '@upl/trust-engine';

export type C2paReadResult = ManifestStore | null;

const SOURCE = { adapter: 'C2PAAdapter', provider: 'C2PA/CAI SDK' } as const;
const TRUST_POLICY = {
  name: 'C2PA SDK default validation',
  version: 'c2pa-normalizer@1.0.0',
  trustedSignerIds: [],
  knownSignerIds: [],
} as const;

export interface C2paNormalizationRuntime {
  mode: VerificationResult['processing']['mode'];
  sdkName: string;
  sdkVersion: string;
  privacy: VerificationResult['privacy'];
}

export const BROWSER_C2PA_RUNTIME: Readonly<C2paNormalizationRuntime> = Object.freeze({
  mode: 'LOCAL_BROWSER',
  sdkName: '@contentauth/c2pa-web',
  sdkVersion: '0.13.4',
  privacy: {
    processed_locally: true,
    uploaded: false,
    stored: false,
    original_media_retained: false,
  },
});

export async function normalizeC2paEvidence(
  input: ArtifactInput,
  context: AdapterContext,
  store: C2paReadResult,
  durationMs: number,
  runtime: Readonly<C2paNormalizationRuntime> = BROWSER_C2PA_RUNTIME,
): Promise<VerificationResult> {
  const completedAt = new Date().toISOString();
  const sha256 = await sha256Hex(input.bytes);
  const artifactId = stableEvidenceId('artifact', sha256);
  const artifact = {
    id: artifactId,
    filename: input.filename,
    media_type: input.detectedMediaType,
    format: input.detectedMediaType === 'image/png' ? 'PNG' : 'JPEG',
    byte_size: input.bytes.byteLength,
    sha256,
    magic_bytes_valid: true,
  };
  const universalLimitations = [
    {
      id: 'limitation:not-factual-truth',
      code: 'NOT_FACTUAL_TRUTH',
      message_key: 'limitations.not_factual_truth',
      evidence_refs: [],
    },
    {
      id: 'limitation:r1-signal-scope',
      code: 'R1_SIGNAL_SCOPE',
      message_key: 'limitations.r1_signal_scope',
      evidence_refs: [],
    },
  ];
  const activeLabel = store?.active_manifest ?? undefined;
  const manifests = store?.manifests ?? {};
  const activeManifest = (activeLabel ? manifests[activeLabel] : undefined) ?? Object.values(manifests)[0];

  if (!store || !activeManifest) {
    const signalId = stableEvidenceId('signal', `${sha256}:c2pa:absent`);
    return verificationResultSchema.parse({
      verification_id: context.verificationId,
      schema_version: SCHEMA_VERSION,
      artifact,
      overall_status: 'NO_SUPPORTED_PROVENANCE',
      signals: [{
        id: signalId,
        source: SOURCE,
        evidence_refs: [],
        kind: 'C2PA_MANIFEST',
        state: 'ABSENT',
        label: 'No C2PA manifest found',
        details: {},
      }],
      credentials: [],
      claims: [],
      assertions: [],
      actions: [],
      actors: [],
      tools: [],
      evidence_graph: {
        edges: [{
          id: stableEvidenceId('edge', `${signalId}:${artifactId}`),
          from: signalId,
          to: artifactId,
          relation: 'DESCRIBES',
          evidence_refs: [],
        }],
      },
      trust: {
        policy_name: TRUST_POLICY.name,
        policy_version: TRUST_POLICY.version,
        evaluated_at: completedAt,
        statuses: ['TRUST_STATUS_UNKNOWN'],
        certificate_chain_validity: 'NOT_APPLICABLE',
        trust_list_membership: 'NOT_APPLICABLE',
      },
      integrity: {
        manifest_presence: 'ABSENT',
        cryptographic_validity: 'NOT_APPLICABLE',
        asset_binding_validity: 'NOT_APPLICABLE',
      },
      conclusions: [{
        id: 'conclusion:status',
        title_key: 'status.NO_SUPPORTED_PROVENANCE.title',
        explanation_key: 'status.NO_SUPPORTED_PROVENANCE.explanation',
        trace_refs: [signalId],
      }],
      warnings: [],
      limitations: [
        ...universalLimitations,
        {
          id: 'limitation:absence-not-authorship',
          code: 'ABSENCE_NOT_AUTHORSHIP',
          message_key: 'limitations.absence_not_authorship',
          evidence_refs: [signalId],
        },
      ],
      timestamps: {
        verification_started_at: context.startedAt,
        verification_completed_at: completedAt,
      },
      processing: {
        mode: runtime.mode,
        duration_ms: durationMs,
        adapters: [{ name: runtime.sdkName, version: runtime.sdkVersion }],
        errors: [],
      },
      privacy: runtime.privacy,
      capabilities: VERIFICATION_CAPABILITIES,
      raw_provider_evidence: { c2pa: { manifest_present: false } },
    });
  }

  const validationStatuses = collectValidationStatuses(store);
  const validationState = store.validation_state ?? undefined;
  const status = overallStatus(validationState);
  const signatureValidity = deriveSignatureValidity(validationState, validationStatuses);
  const assetBindingValidity = deriveAssetBindingValidity(validationState, validationStatuses);
  const credentialId = stableEvidenceId('credential', `${sha256}:${activeLabel ?? 'active'}`);
  const claimId = stableEvidenceId('claim', `${credentialId}:claim`);
  const manifestSignalId = stableEvidenceId('signal', `${sha256}:c2pa:present`);
  const signature = activeManifest.signature_info;
  const signerName = signature?.common_name ?? signature?.issuer ?? undefined;
  const signerId = signerName ? stableEvidenceId('actor', signerName) : undefined;
  const signatureRecord = isRecord(signature) ? signature : {};
  const signatureAlgorithm = typeof signatureRecord.alg === 'string'
    ? signatureRecord.alg
    : typeof signatureRecord.algorithm === 'string' ? signatureRecord.algorithm : undefined;
  const actor = buildActor(signerId, signerName, signature?.issuer ?? undefined, signatureAlgorithm, validationState);
  const tools = buildTools(activeManifest, claimId);
  const assertions = buildAssertions(activeManifest, claimId);
  const actions = buildActions(activeManifest, claimId, signerId, tools[0]?.id);
  const trust = evaluateTrust(
    {
      evaluatedAt: completedAt,
      signatureValidity,
      certificateChainValidity: validationState === 'Trusted' ? 'VALID' : 'UNKNOWN',
      ...(signerId ? { signerId } : {}),
      ...(signerName ? { signerIdentity: signerName } : {}),
      credentialExpired: validationStatuses.some(({ code }) => /expired/i.test(code)),
      credentialRevoked: signature?.revocation_status === true,
      policyTrustDecision: validationState === 'Trusted' ? 'VALID' : 'UNKNOWN',
    },
    TRUST_POLICY,
  );
  const actors = actor ? [actor] : [];
  const edges = buildEdges({
    artifactId,
    credentialId,
    claimId,
    ...(signerId ? { signerId } : {}),
    assertions,
    actions,
    tools,
    manifestSignalId,
  });
  const failureStatuses = validationStatuses.filter(({ success, code }) => success === false || /mismatch|invalid|malformed|error/i.test(code));

  return verificationResultSchema.parse({
    verification_id: context.verificationId,
    schema_version: SCHEMA_VERSION,
    artifact,
    overall_status: status,
    signals: [
      {
        id: manifestSignalId,
        source: SOURCE,
        evidence_refs: [credentialId],
        kind: 'C2PA_MANIFEST',
        state: validationState === 'Invalid' ? 'INVALID' : validationState ? 'VALID' : 'PRESENT',
        label: 'C2PA manifest detected',
        details: {
          validation_state: validationState ?? 'Unknown',
          active_manifest: activeLabel ?? null,
          validation_event_count: validationStatuses.length,
        },
      },
      ...validationStatuses.slice(0, 50).map((entry, index) => ({
        id: stableEvidenceId('signal', `${credentialId}:validation:${index}:${entry.code}`),
        source: SOURCE,
        evidence_refs: [credentialId],
        kind: 'C2PA_VALIDATION',
        state: entry.success === false ? 'INVALID' as const : entry.success === true ? 'VALID' as const : 'UNKNOWN' as const,
        label: entry.code,
        details: entry.explanation ? { explanation: safeString(entry.explanation) } : {},
      })),
    ],
    credentials: [{
      id: credentialId,
      source: SOURCE,
      evidence_refs: [manifestSignalId],
      kind: 'C2PA_CONTENT_CREDENTIAL',
      manifest_present: true,
      cryptographic_validity: signatureValidity,
      asset_binding_validity: assetBindingValidity,
      ...(signerId ? { signer_actor_id: signerId } : {}),
      ...(isIsoTimestamp(signature?.time) ? { issued_at: signature.time } : {}),
    }],
    claims: [{
      id: claimId,
      source: SOURCE,
      evidence_refs: [credentialId],
      credential_id: credentialId,
      label: activeLabel ?? activeManifest.label ?? 'Active C2PA claim',
      value: sanitizeValue({
        title: activeManifest.title,
        format: activeManifest.format,
        claim_version: activeManifest.claim_version,
      }),
    }],
    assertions,
    actions,
    actors,
    tools,
    evidence_graph: { edges },
    trust,
    integrity: {
      manifest_presence: 'PRESENT',
      cryptographic_validity: signatureValidity,
      asset_binding_validity: assetBindingValidity,
    },
    conclusions: [{
      id: 'conclusion:status',
      title_key: `status.${status}.title`,
      explanation_key: `status.${status}.explanation`,
      trace_refs: [manifestSignalId, credentialId],
    }],
    warnings: failureStatuses.slice(0, 20).map((entry, index) => ({
      id: stableEvidenceId('warning', `${credentialId}:${index}:${entry.code}`),
      code: entry.code,
      message_key: 'warnings.c2pa_validation_failure',
      evidence_refs: [credentialId],
    })),
    limitations: universalLimitations,
    timestamps: {
      verification_started_at: context.startedAt,
      verification_completed_at: completedAt,
    },
    processing: {
      mode: runtime.mode,
      duration_ms: durationMs,
      adapters: [{ name: runtime.sdkName, version: runtime.sdkVersion }],
      errors: [],
    },
    privacy: runtime.privacy,
    capabilities: VERIFICATION_CAPABILITIES,
    raw_provider_evidence: {
      c2pa: sanitizeValue({
        active_manifest: activeLabel,
        validation_state: validationState,
        validation_status: validationStatuses,
        manifest: activeManifest,
      }),
    },
  });
}

function overallStatus(state: ManifestStore['validation_state']): OverallStatus {
  if (state === 'Trusted') return 'VERIFIED_PROVENANCE';
  if (state === 'Valid') return 'PROVENANCE_PRESENT';
  if (state === 'Invalid') return 'INVALID_OR_TAMPERED';
  return 'INCONCLUSIVE';
}

function collectValidationStatuses(store: ManifestStore): ValidationStatus[] {
  const active = store.validation_results?.activeManifest;
  if (active) return [...active.success, ...active.informational, ...active.failure];
  return store.validation_status ?? [];
}

function deriveSignatureValidity(state: ManifestStore['validation_state'], statuses: ValidationStatus[]): Validity {
  if (state === 'Trusted' || state === 'Valid') return 'VALID';
  if (statuses.some(({ code }) => /claimSignature.*(mismatch|invalid)|signingCredential.*invalid/i.test(code))) return 'INVALID';
  return 'UNKNOWN';
}

function deriveAssetBindingValidity(state: ManifestStore['validation_state'], statuses: ValidationStatus[]): Validity {
  if (state === 'Trusted' || state === 'Valid') return 'VALID';
  if (statuses.some(({ code }) => /(dataHash|bmffHash|boxHash|hashedURI).*(mismatch|invalid)/i.test(code))) return 'INVALID';
  return 'UNKNOWN';
}

function buildActor(
  id: string | undefined,
  name: string | undefined,
  issuer: string | undefined,
  signatureAlgorithm: string | undefined,
  state: ManifestStore['validation_state'],
): Actor | undefined {
  if (!id || !name) return undefined;
  return {
    id,
    source: SOURCE,
    evidence_refs: [],
    name: safeString(name),
    identity: safeString(name),
    ...(issuer ? { issuer: safeString(issuer) } : {}),
    ...(signatureAlgorithm ? { signature_algorithm: safeString(signatureAlgorithm) } : {}),
    identity_status: state === 'Trusted' ? 'KNOWN' : 'UNKNOWN',
  };
}

function buildTools(manifest: Manifest | undefined, claimId: string): Tool[] {
  const values = manifest?.claim_generator_info?.map(({ name, version }) => ({ name, version: version ?? undefined })) ?? [];
  if (values.length === 0 && manifest?.claim_generator) values.push({ name: manifest.claim_generator, version: undefined });
  return values.slice(0, 12).map(({ name, version }, index) => ({
    id: stableEvidenceId('tool', `${claimId}:${index}:${name}`),
    source: SOURCE,
    evidence_refs: [claimId],
    name: safeString(name),
    ...(version ? { version: safeString(version) } : {}),
  }));
}

function buildAssertions(manifest: Manifest | undefined, claimId: string): Assertion[] {
  const assertions = (manifest?.assertions ?? []).slice(0, 80).map((assertion, index) => ({
    id: stableEvidenceId('assertion', `${claimId}:${index}:${assertion.label}`),
    source: { ...SOURCE, native_id: `${assertion.label}${assertion.instance ? `:${assertion.instance}` : ''}` },
    evidence_refs: [claimId],
    claim_id: claimId,
    label: safeString(assertion.label),
    value: sanitizeValue(assertion.data),
  }));
  for (const [index, ingredient] of (manifest?.ingredients ?? []).slice(0, 24).entries()) {
    assertions.push({
      id: stableEvidenceId('assertion', `${claimId}:ingredient:${index}`),
      source: { ...SOURCE, native_id: ingredient.label ?? `ingredient-${index}` },
      evidence_refs: [claimId],
      claim_id: claimId,
      label: 'c2pa.ingredient',
      value: sanitizeValue(ingredient),
    });
  }
  return assertions;
}

function buildActions(manifest: Manifest | undefined, claimId: string, actorId: string | undefined, toolId: string | undefined): Action[] {
  const output: Action[] = [];
  for (const assertion of manifest?.assertions ?? []) {
    if (!/c2pa\.actions/i.test(assertion.label) || !isRecord(assertion.data) || !Array.isArray(assertion.data.actions)) continue;
    for (const action of assertion.data.actions.slice(0, 80)) {
      if (!isRecord(action) || typeof action.action !== 'string') continue;
      const index = output.length;
      const sourceType = typeof action.digitalSourceType === 'string'
        ? action.digitalSourceType
        : typeof action.digital_source_type === 'string' ? action.digital_source_type : undefined;
      output.push({
        id: stableEvidenceId('action', `${claimId}:${index}:${action.action}`),
        source: SOURCE,
        evidence_refs: [claimId],
        action_type: safeString(action.action),
        ...(isIsoTimestamp(action.when) ? { occurred_at: action.when } : {}),
        ...(sourceType ? { source_type: safeString(sourceType) } : {}),
        ...(actorId ? { actor_id: actorId } : {}),
        ...(toolId ? { tool_id: toolId } : {}),
      });
    }
  }
  return output;
}

function buildEdges(input: {
  artifactId: string;
  credentialId: string;
  claimId: string;
  signerId?: string;
  assertions: Assertion[];
  actions: Action[];
  tools: Tool[];
  manifestSignalId: string;
}): EvidenceEdge[] {
  const edges: EvidenceEdge[] = [];
  const add = (from: string, to: string, relation: EvidenceEdge['relation']): void => {
    edges.push({ id: stableEvidenceId('edge', `${from}:${relation}:${to}`), from, to, relation, evidence_refs: [] });
  };
  add(input.manifestSignalId, input.artifactId, 'DESCRIBES');
  add(input.artifactId, input.credentialId, 'CONTAINS');
  add(input.credentialId, input.claimId, 'CONTAINS');
  if (input.signerId) add(input.credentialId, input.signerId, 'SIGNED_BY');
  for (const assertion of input.assertions) add(input.claimId, assertion.id, 'CONTAINS');
  for (const tool of input.tools) add(input.claimId, tool.id, 'CREATED_WITH');
  for (const action of input.actions) {
    add(input.claimId, action.id, 'CONTAINS');
    if (action.actor_id) add(action.id, action.actor_id, 'PERFORMED_BY');
    if (action.tool_id) add(action.id, action.tool_id, 'CREATED_WITH');
  }
  return edges;
}

export function sanitizeValue(value: unknown, depth = 0): unknown {
  if (value === null || typeof value === 'boolean' || typeof value === 'number') return value;
  if (typeof value === 'string') return safeString(value);
  if (value instanceof Date) return value.toISOString();
  if (depth >= 6) return '[depth limit]';
  if (Array.isArray(value)) return value.slice(0, 80).map((entry) => sanitizeValue(entry, depth + 1));
  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer || value instanceof Blob) return '[binary omitted]';
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 120)
        .map(([key, entry]) => [safeString(key, 120), sanitizeValue(entry, depth + 1)]),
    );
  }
  return '[unsupported value]';
}

function safeString(value: string, length = 1000): string {
  return stripControlCharacters(value).slice(0, length);
}

function stripControlCharacters(value: string): string {
  return Array.from(value).filter((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
  }).join('');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isIsoTimestamp(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) && /(?:Z|[+-]\d{2}:\d{2})$/.test(value);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const safeBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', safeBuffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function normalizeC2paEvidenceFailure(
  input: ArtifactInput,
  context: AdapterContext,
  durationMs: number,
  runtime: Readonly<C2paNormalizationRuntime> = BROWSER_C2PA_RUNTIME,
): Promise<VerificationResult> {
  const base = await normalizeC2paEvidence(input, context, null, durationMs, runtime);
  const signal = base.signals[0];
  if (!signal) throw new Error('C2PA failure result is missing its signal');
  signal.state = 'UNKNOWN';
  signal.label = 'C2PA inspection could not be completed';
  base.overall_status = 'INCONCLUSIVE';
  base.integrity.manifest_presence = 'UNKNOWN';
  base.integrity.cryptographic_validity = 'UNKNOWN';
  base.integrity.asset_binding_validity = 'UNKNOWN';
  base.conclusions[0] = {
    id: 'conclusion:status',
    title_key: 'status.INCONCLUSIVE.title',
    explanation_key: 'status.INCONCLUSIVE.explanation',
    trace_refs: [signal.id],
  };
  base.warnings.push({
    id: 'warning:c2pa-parser-failure',
    code: 'C2PA_PARSER_FAILURE',
    message_key: 'warnings.c2pa_parser_failure',
    evidence_refs: [signal.id],
  });
  base.processing.errors.push({ code: 'C2PA_PARSER_FAILURE', adapter: runtime.sdkName });
  base.raw_provider_evidence = { c2pa: { inspection_completed: false } };
  return verificationResultSchema.parse(base);
}
